import moment, { Moment } from "moment";
import { EPeriodTypes } from "./exception.types";
import { IDateEntry } from "@lib/types";
import {
  fieldMapping,
  formConfig,
  getSelectOptions,
  TSelectHelper,
} from "./exception-form.constants";
import { formatValue } from "../modal.helpers";
import { renderPeriodFormByType } from "./exception.templates";
import i18next from "i18next";

export type TErrorField = 'day' | 'month' | 'year' | 'weekDay' | 'calendarId' | '*';
export type TError = Array<{text: string, fields: TErrorField | TErrorField[]}>;

export class SelectHelper {
  selectTypes: TSelectHelper;
  fields: Record<string, HTMLSelectElement | HTMLInputElement> = {};
  periodType: EPeriodTypes | null = null;
  container: HTMLElement;
  onValidate: () => void;

  constructor(onValidate: () => void) {
    this.selectTypes = getSelectOptions();
    this.onValidate = onValidate;
  }

  setPeriodType(type: EPeriodTypes) {
    this.periodType = type;
  }

  isSpecialMonth(month: string) {
    return parseInt(month) === 13 || parseInt(month) === 14;
  }

  isSpecialDay(day: string){
    return parseInt(day) === 33 || parseInt(day) === 34;
  }
  
  /**
   * Валидирует поля формы в зависимости от текущего типа периода.
   * @returns {string | null} - Возвращает текст ошибки или null, если ошибок нет.
   */
  isValidFields(currentDate: Moment): string | null {
    if (this.periodType === undefined || this.periodType === null) {
      throw new Error(i18next.t($ => $.scheduleBacnet.errorPeriodTypeNotChosen));
    }

    const errors: TError = [];

    /**
     * Преобразует части даты в Moment объект с подстановкой текущих значений для wildcards
     */
    const toMomentDate = (dateParts: {
      weekDay: string | number;
      day: string | number;
      month: string | number;
      year: string | number;
    }): Moment | null => {
      const year = dateParts.year === "****" ? currentDate.year() : parseInt(dateParts.year.toString());
      const month = dateParts.month === "**" ? currentDate.month() + 1 : parseInt(dateParts.month.toString());
      const day = dateParts.day === "**" ? currentDate.date() : parseInt(dateParts.day.toString());

      if (parseInt(dateParts.day.toString()) === 32) return moment([year, month - 1]).endOf("month");
      return moment([year, month - 1, day]);
    };

    /**
     * Проверяет валидность конкретной даты
     */
    const validateConcreteDate = (dateParts: {
      weekDay: string;
      day: string;
      month: string;
      year: string;
    }, isRange: boolean = false): boolean => {
      // Для неконкретной даты - пропускаем валидацию
      if (dateParts.month === "**" && dateParts.year === "****") return true;
      // Пропускаем валидацию для чет\нечет
      if (dateParts.month === "**" || (parseInt(dateParts.month) >= 13 && parseInt(dateParts.month) <= 14)) return true; 
      if (parseInt(dateParts.day) >= 33 && parseInt(dateParts.day) <= 34) return true;

      const day = parseInt(dateParts.day) === 32 ? currentDate.daysInMonth() : dateParts.day;
      
      // Валидация на корректность дней в месяце (на 30-31 и февраль 28-29)
      if (dateParts.month !== "**" && day !== "**") {
        const momentDate = toMomentDate({
          ...dateParts, 
          day,
          year: dateParts.year === "****" ? 2028 : parseInt(dateParts.year) // Fake високосный год для проверки всех дней в месяцах (в т.ч. в феврале 29 дней)
        });
        
        if (!momentDate || !momentDate.isValid()) {
          errors.push({
            text: i18next.t($ => $.scheduleBacnet.errorIncorrectDayOfMonth),
            fields: ['day']
          });
          return;
        }
      }

      // Проверка дня недели, если заполнены все поля
      const momentDate = toMomentDate(dateParts); 
      
      if (
        dateParts.weekDay !== "*" && 
        day !== "**" &&
        dateParts.month !== "**" &&
        dateParts.year !== "****" && 
        !isRange
      ) {
        if (momentDate.isoWeekday() !== parseInt(dateParts.weekDay)) {
          errors.push({
            text: i18next.t($ => $.scheduleBacnet.errorIncorrectDayOfWeek),
            fields: ['weekDay']
          });
          return;
        }
      }
      
      return true;
    };

    /**
     * Проверяет что дата начала не позже даты конца
     */
    const validateDateOrder = (
      startParts: { day: string; month: string; year: string },
      endParts: { day: string; month: string; year: string }
    ): boolean => {
      const startYear = startParts.year === "****" ? 1900 : parseInt(startParts.year.toString());
      const startMonth = startParts.month === "**" ? 1 : parseInt(startParts.month.toString());

      const endYear = endParts.year === "****" ? 2255 : parseInt(endParts.year.toString());
      const endMonth = endParts.month === "**" ? 12 : parseInt(endParts.month.toString());

      const startDate = toMomentDate({
        weekDay: "*",
        day: startParts?.day,
        month: startMonth,
        year: startYear,
      });
      const endDate = toMomentDate({
        weekDay: "*",
        day: endParts?.day,
        month: endMonth,
        year: endYear,
      });
      
      // Проверка последовательности дней: если не любой день
      if (
        startParts?.day !== "**" && 
        endParts?.day !== "**" &&
        !this.isSpecialDay(startParts.day) &&
        !this.isSpecialDay(endParts.day) &&
        startParts?.day > endParts?.day
      ) {
        errors.push({
          text: i18next.t($ => $.scheduleBacnet.errorStartDateBeforeEndDate),
          fields: ['day']
        });
        return;
      }
      // Проверка последовательности месяцев: если не любой месяц
      if (
        startParts?.month !== "**" && 
        endParts?.month !== "**" && 
        startParts?.month !== "**" && 
        endParts?.month !== "**" &&
        !['13', '14'].includes(startParts?.month) &&
        !['13', '14'].includes(endParts?.month) &&
        startParts?.month > endParts?.month
      ) {
        errors.push({
          text: i18next.t($ => $.scheduleBacnet.errorStartDateBeforeEndDate),
          fields: ['month']
        });
        return;
      }

      if (startDate && endDate && startDate.isAfter(endDate)) {
        errors.push({
          text: i18next.t($ => $.scheduleBacnet.errorStartDateBeforeEndDate),
          fields: "*"
        });
      }
    };

    switch (this.periodType) {
      case EPeriodTypes.DATE: {
        const dateParts = {
          weekDay: this.fields?.dayOfWeek?.value || "*",
          day: formatValue(this.fields?.dayOfMonth?.value, 2) || "**",
          month: formatValue(this.fields?.month?.value, 2) || "**",
          year: formatValue(this.fields?.year?.value, 4) || "****",
        };

        validateConcreteDate(dateParts) 
        break;
      }

      case EPeriodTypes.DATE_RANGE: {
        const startDateParts = {
          weekDay: this.fields?.startDayOfWeek?.value || "*",
          day: formatValue(this.fields?.startDayOfMonth?.value, 2) || "**",
          month: formatValue(this.fields?.startMonth?.value, 2) || "**",
          year: formatValue(this.fields?.startYear?.value, 4) || "****",
        };

        const endDateParts = {
          weekDay: this.fields?.endDayOfWeek?.value || "*",
          day: formatValue(this.fields?.endDayOfMonth?.value, 2) || "**",
          month: formatValue(this.fields?.endMonth?.value, 2) || "**",
          year: formatValue(this.fields?.endYear?.value, 4) || "****",
        };

        // Валидация отдельных дат
        validateConcreteDate(startDateParts, true)
        validateConcreteDate(endDateParts, true)
 
        // Проверка для дней
        if ((this.isSpecialDay(startDateParts.day) || this.isSpecialDay(endDateParts.day)) && startDateParts.day !== endDateParts.day) {
          errors.push({
            text: i18next.t($ => $.scheduleBacnet.errorDaysOfMonthDoNotMatch),
            fields: ['day']
          });
        }

        // Проверка для месяцев
        if ((this.isSpecialMonth(startDateParts.month) || this.isSpecialMonth(endDateParts.month)) && startDateParts.month !== endDateParts.month) {
          errors.push({
            text: i18next.t($ => $.scheduleBacnet.errorMonthsDoNotMatch),
            fields: ['month']
          });
        }

        // Проверка порядка дат
        validateDateOrder(
          {
            day: startDateParts.day,
            month: startDateParts.month,
            year: startDateParts.year,
          },
          {
            day: endDateParts.day,
            month: endDateParts.month,
            year: endDateParts.year,
          }
        )


        break;
      }

      case EPeriodTypes.WEEK_AND_DAY: {
        break;
      }

      case EPeriodTypes.CAL_REF: {
        const calendarId = this.fields["calendarId"]?.value;
        if (!calendarId) {
          errors.push({
            text: i18next.t($ => $.scheduleBacnet.errorNeedToChooseCalendarId),
            fields: ["calendarId"]
          });
        }
        break;
      }

      default:
        throw new Error("Неизвестный тип формы");
    }

    this.highlightErrorFields(errors);
    return errors.length > 0 ? errors?.map(x => x.text)?.join("; ") : null;
  }

  /**
   * Подсветка полей с ошибками
   * @param {TError} errors Массив ошибок
   */
  highlightErrorFields(errors: TError) {
    // Если нет ошибок - убираем подстветку
    if (!errors || errors.length === 0) {
      Object.values(this.fields).forEach(field => {
        field.classList.remove("error");
      });
    }
    
    // Применяем подсветки для каждой ошибки
    errors?.forEach(err => {
      const fieldsToHighlight = Array.isArray(err.fields) ? err.fields : [err.fields];
      
      fieldsToHighlight.forEach(fieldType => {
        if (fieldType === '*') {
          // Подсвечиваем все поля
          Object.values(this.fields).forEach(field => {
            field.classList.add("error");
          });
        } else {
          // Подсвечиваем только связанные поля
          const relatedFields = fieldMapping[fieldType];
          relatedFields.forEach(fieldName => {
            const field = this.fields[fieldName];
            if (field) {
              field.classList.add("error");
            }
          });
        }
      });
    });
  }

  /**
   * Создает селект или инпут и добавляет его в контейнер
   * @param type Тип элемента (селект или инпут).
   * @param id Идентификатор элемента.
   * @param initialValue Начальное значение.
   */
  createField(
    type: keyof TSelectHelper | "input",
    id: string,
    initialValue?: string,
    container?: HTMLElement
  ) {
    let field: HTMLSelectElement | HTMLInputElement;
    let close: null | HTMLDivElement

    if (type === "input") {
      field = document.createElement("input");
      field.type = "number";
      field.value = initialValue || "";
    } else {
      field = document.createElement("select");

      this.selectTypes[type].forEach((optionData) => {
        const option = document.createElement("option");
        option.value = optionData.value;
        option.textContent = optionData.text;
        field.appendChild(option);
      });

      field.value = initialValue || this.selectTypes[type][0].value;
      
      if (type === "year") {
        field.addEventListener("change", () =>
          this.handleYearChange(field as HTMLSelectElement)
        );
        
        // Редактирование: любой год в селекте или конкретный в инпуте
        if (initialValue) {
          if (initialValue === "****") {
            field.value = this.selectTypes[type][0].value;
            // field.disabled = true;
          } else {
            field = document.createElement("input");
            field.type = "number";
            field.value = initialValue || "";
            // field.disabled = true;
            close = document.createElement("div");
            close.textContent = "✕"
            close.style.marginLeft = "6px"
            close.addEventListener("click", () => {
              const container = close.parentElement;
              this.replaceYearInputWithSelect(id, container, "****");
            })
          }
        }  
      }
    }
    field.id = id;
    // field.disabled = Boolean(initialValue);

    if(container){
      container.appendChild(field);
      if(close) container.appendChild(close);
    } else {
      this.container.appendChild(field);
      if(close) this.container.appendChild(close);
    }
    this.fields[id] = field;
    field.addEventListener('change', () => {
      this.onValidate();
    })
  }

  /**
   * Обрабатывает изменение значения в поле "год".
   * @param {HTMLSelectElement} select - Селект года
   */
  handleYearChange(select: HTMLSelectElement) {
    const container = select.parentElement;

    if (container && select.value === "custom") {
      this.replaceSelectWithYearInput(select.id, container, "");
    } else {
      this.replaceYearInputWithSelect(select.id, container, select.value);
    }
  }

  /**
   * Заменяет селект на инпут для выбора года
   * @param {string} id - Идентификатор инпута
   * @param {HTMLElement} container - Контейнер, в котором находится селект
   * @param {string} initialValue - Начальное значение для инпута.
   */
  replaceSelectWithYearInput(
    id: string,
    container: HTMLElement,
    initialValue?: string
  ) {
    const yearInput = document.createElement("input");
    yearInput.type = "number";
    yearInput.placeholder = i18next.t($ => $.scheduleBacnet.enterYear);
    yearInput.min = "1900";
    yearInput.max = "2255";
    yearInput.id = id;
    yearInput.value = initialValue || new Date().getFullYear().toString();

    container.innerHTML = "";
    container.appendChild(yearInput);

    const close = document.createElement("div");
    close.textContent = "✕"
    close.style.marginLeft = "6px"
    close.addEventListener("click", () => {
      const container = close.parentElement;
      this.replaceYearInputWithSelect(id, container, "****");
    })
    container.appendChild(close);

    this.fields[id] = yearInput;  
  }

  /**
   * Заменяет инпут на селект для выбора года.
   * @param {string} id - Идентификатор селекта.
   * @param {HTMLElement} container - Контейнер, в котором находится инпут.
   * @param {string} initialValue - Начальное значение для селекта.
   */
  replaceYearInputWithSelect(
    id: string,
    container: HTMLElement,
    initialValue?: string
  ) {
    container.innerHTML = "";
    this.createField("year", id, initialValue, container);
  }

  /**
   * Создает инпут
   * @param {string} id - Идентификатор
   * @param {HTMLElement} container - Родитель, в который будет вставлен инпут.
   * @param {string} initialValue - Начальное значение
   */
  createInput(id: string, container: HTMLElement, initialValue?: string) {
    const input = document.createElement("input");
    input.type = "number";
    input.id = id;
    input.value = initialValue || "";

    container.innerHTML = "";
    container.appendChild(input);

    this.fields[id] = input;
  }

  /**
   * Собирает значения с формы и возвращает объект, соответствующий типу периода.
   * @returns {IDateEntry} - Объект с данными формы.
   */
  getFormValues(): IDateEntry {
    if (this.periodType === undefined || this.periodType === null) {
      throw new Error(i18next.t($ => $.scheduleBacnet.errorPeriodTypeNotChosen));
    }

    const result: IDateEntry = {
      type: this.periodType,
      start: {
        weekDay: "*",
        day: "*",
        week: "*",
        month: "**",
        year: "****",
      },
      end: {
        weekDay: "*",
        day: "*",
        week: "*",
        month: "**",
        year: "****",
      },
    };

    switch (this.periodType) {
      case EPeriodTypes.DATE: {
        result.start = {
          weekDay: parseInt(this.fields["dayOfWeek"]?.value) || "*",
          day: parseInt(this.fields["dayOfMonth"]?.value) || "**",
          month: parseInt(this.fields["month"]?.value) || "**",
          year: parseInt(this.fields["year"]?.value) || "****",
        };
        break;
      }

      case EPeriodTypes.DATE_RANGE: {
        result.start = {
          weekDay: parseInt(this.fields["startDayOfWeek"]?.value) || "*",
          day: parseInt(this.fields["startDayOfMonth"]?.value) || "**",
          month: parseInt(this.fields["startMonth"]?.value) || "**",
          year: parseInt(this.fields["startYear"]?.value) || "****",
        };

        result.end = {
          weekDay: parseInt(this.fields["endDayOfWeek"]?.value) || "*",
          day: parseInt(this.fields["endDayOfMonth"]?.value) || "**",
          month: parseInt(this.fields["endMonth"]?.value) || "**",
          year: parseInt(this.fields["endYear"]?.value) || "****",
        };
        break;
      }

      case EPeriodTypes.WEEK_AND_DAY: {
        result.start = {
          week: parseInt(this.fields["periodWeek"]?.value) || "*",
          weekDay: parseInt(this.fields["periodDayOfWeek"]?.value) || "*",
          month: parseInt(this.fields["periodMonth"]?.value) || "**",
        };
        break;
      }

      case EPeriodTypes.CAL_REF: {
        result.calRef = this.fields["calendarId"]?.value || "";
        break;
      }

      default:
        throw new Error("Неизвестный тип формы");
    }

    return result;
  }

  /**
   * Создает селекты для указанного типа формы.
   * @param {EPeriodTypes} type - Тип формы.
   * @param {HTMLElement} container - Контейнер, в который вставляется форма.
   * @param {IDateEntry} initialData - Начальные данные для заполнения формы.
   */
  renderForm(
    type: EPeriodTypes,
    container: HTMLElement,
    periodForm: HTMLElement,
    initialData?: IDateEntry
  ) {
    this.setPeriodType(type);

    // Схема полей
    const fields = formConfig(initialData)[this.periodType] || [];

    container.innerHTML = renderPeriodFormByType(this.periodType);

    fields.forEach((field) => {
      const containerElement = periodForm.querySelector(
        `#${field.containerId}`
      );
      if (!containerElement) return;
      this.container = containerElement as HTMLElement;

      this.createField(field.type, field.id, field.value ?? "");
    });
  }
}
